'use client';

import React, { useState } from 'react';

export interface InputProps {
  label?: string;
  hint?: string;
  /** Error message — also turns the border/ring brick-red. */
  error?: string;
  /** Node shown at the inline-start inside the field (e.g. 🇮🇱 +972). */
  leading?: React.ReactNode;
  /** Force LTR + bidi-isolation for phone numbers, handles, emails in an RTL screen. */
  latin?: boolean;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
}

export function Input({
  label,
  hint,
  error,
  leading = null,
  latin = false,
  value,
  onChange,
  placeholder,
  type = 'text',
  disabled = false,
  style = {},
  ...rest
}: InputProps & Omit<React.InputHTMLAttributes<HTMLInputElement>, keyof InputProps>) {
  const [focused, setFocused] = useState(false);
  const borderColor = error ? 'var(--error)' : focused ? 'var(--brand)' : 'var(--border-strong)';

  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
        fontFamily: 'var(--font-sans)',
        ...style,
      }}
    >
      {label && (
        <span
          style={{
            fontSize: 'var(--fs-label)',
            fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'],
            color: 'var(--text-primary)',
          }}
        >
          {label}
        </span>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-3)',
          height: 'var(--control-h)',
          paddingInline: 'var(--sp-5)',
          background: disabled ? 'var(--bg-sunken)' : 'var(--bg-surface)',
          border: `1px solid ${borderColor}`,
          borderRadius: 'var(--r-sm)',
          boxShadow: focused ? '0 0 0 3px var(--brand-wash)' : 'none',
          transition: 'border-color var(--dur-micro), box-shadow var(--dur-micro)',
        }}
      >
        {leading && (
          <span style={{ color: 'var(--text-secondary)', flex: '0 0 auto' }}>{leading}</span>
        )}
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          dir={latin ? 'ltr' : undefined}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            flex: 1,
            minWidth: 0,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-body-l)',
            color: 'var(--text-primary)',
            textAlign: latin ? 'start' : undefined,
            unicodeBidi: latin ? 'isolate' : undefined,
          }}
          {...rest}
        />
      </div>
      {(hint || error) && (
        <span
          style={{
            fontSize: 'var(--fs-caption)',
            color: error ? 'var(--error)' : 'var(--text-tertiary)',
          }}
        >
          {error || hint}
        </span>
      )}
    </label>
  );
}
